import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const scrollToSection = (id) => {
    setOpen(false);
    if (id === "about") {
      navigate("/about");
      return;
    }
    if (id === "resources") {
      navigate("/insights");
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      window.history.pushState(null, "", `/#${id}`);
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.history.pushState(null, "", "/");
    }
  };

  const anchorLinks = [
    { id: "top",       label: "Home" },
    { id: "problem",   label: "Problem" },
    { id: "solution",  label: "Solution" },
    { id: "benefits",  label: "Benefits" },
    { id: "roi",       label: "ROI" },
    { id: "resources", label: "Insights" },
    { id: "about",     label: "About" },
  ];

  const anchorBtnCls = (id) => {
    const active =
      location.pathname === "/" &&
      (location.hash === `#${id}` || (id === "top" && !location.hash));
    return `px-2.5 py-2 text-[13px] font-semibold tracking-tight transition-colors ${
      active ? "text-[#C62828]" : "text-slate-700 hover:text-[#17324D]"
    }`;
  };

  const leftAnchors  = anchorLinks.slice(0, 3);
  const rightAnchors = anchorLinks.slice(3);

  return (
    <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/70" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center h-20 relative">
        <div className="hidden lg:flex flex-1 items-center gap-0.5 justify-start pr-16">
          {leftAnchors.map(l => (
            <button
              key={l.id}
              type="button"
              onClick={() => scrollToSection(l.id)}
              className={anchorBtnCls(l.id)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 lg:absolute lg:left-1/2 lg:-translate-x-1/2 flex-shrink-0">
          <img src="/lumberloop-logo.png" alt="LumberLoop USA" className="h-[60px] w-auto"/>
        </Link>

        <div className="flex-1 lg:hidden"/>

        <div className="hidden lg:flex flex-1 items-center gap-0.5 justify-end pl-16">
          {rightAnchors.map(l => (
            <button
              key={l.id}
              type="button"
              onClick={() => scrollToSection(l.id)}
              className={anchorBtnCls(l.id)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
            >
              {l.label}
            </button>
          ))}

          {user && user.role === "manufacturer" && (
            <NavLink to="/dashboard" className={({isActive}) => `px-3 py-2 text-[13px] font-semibold tracking-tight transition-colors ${isActive ? "text-[#C62828]" : "text-slate-700 hover:text-[#17324D]"}`} data-testid="nav-dashboard">Dashboard</NavLink>
          )}
          {user && user.role === "admin" && (
            <>
              <NavLink to="/admin/leads" className={({isActive}) => `px-3 py-2 text-[13px] font-semibold tracking-tight transition-colors ${isActive ? "text-[#C62828]" : "text-slate-700 hover:text-[#17324D]"}`} data-testid="nav-admin-leads">Leads</NavLink>
              <NavLink to="/admin/demo-requests" className={({isActive}) => `px-3 py-2 text-[13px] font-semibold tracking-tight transition-colors ${isActive ? "text-[#C62828]" : "text-slate-700 hover:text-[#17324D]"}`} data-testid="nav-admin-demo">Demos</NavLink>
              <NavLink to="/admin/scorecard" className={({isActive}) => `px-3 py-2 text-[13px] font-semibold tracking-tight transition-colors ${isActive ? "text-[#C62828]" : "text-slate-700 hover:text-[#17324D]"}`} data-testid="nav-admin-scorecard">Scorecard</NavLink>
              <NavLink to="/admin/manufacturers" className={({isActive}) => `px-3 py-2 text-[13px] font-semibold tracking-tight transition-colors ${isActive ? "text-[#C62828]" : "text-slate-700 hover:text-[#17324D]"}`} data-testid="nav-admin-mfrs">Mfrs</NavLink>
              <NavLink to="/admin/insights" className={({isActive}) => `px-3 py-2 text-[13px] font-semibold tracking-tight transition-colors ${isActive ? "text-[#C62828]" : "text-slate-700 hover:text-[#17324D]"}`} data-testid="nav-admin-insights">Insights</NavLink>
            </>
          )}

          {!user && (
            <NavLink to="/scorecard" className={({isActive}) => `px-3 py-2 text-[13px] font-bold tracking-tight transition-colors ${isActive ? "text-[#C62828]" : "text-[#65A30D] hover:text-[#4D7C0F]"}`} data-testid="nav-scorecard">
              Scorecard
            </NavLink>
          )}

          <div className="w-px h-6 bg-slate-200 mx-2"/>

          {user ? (
            <button type="button" onClick={handleLogout} className="btn-ghost" data-testid="logout-btn">
              <LogOut size={15}/> Logout
            </button>
          ) : (
            <>
              <Link to="/login" className="btn-ghost" data-testid="nav-login">Login</Link>
              <Link to="/contact" className="btn-primary !py-2 !px-4 text-[13px]" data-testid="nav-cta-demo">Request Demo</Link>
            </>
          )}
        </div>

        <button type="button" className="lg:hidden text-[#17324D] p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X size={24}/> : <Menu size={24}/>}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-4 flex flex-col gap-1" data-testid="mobile-menu">
          {anchorLinks.map(l => (
            <button
              key={l.id}
              type="button"
              onClick={() => scrollToSection(l.id)}
              className="text-left px-3 py-2 text-[14px] font-semibold text-slate-700 hover:text-[#C62828]"
            >
              {l.label}
            </button>
          ))}
          <div className="border-t border-slate-200 mt-3 pt-3 flex flex-col gap-2">
            {!user && (
              <Link to="/scorecard" onClick={() => setOpen(false)} className="text-center px-3 py-2 text-[14px] font-bold text-[#65A30D] hover:bg-lime-50 rounded" data-testid="mobile-nav-scorecard">
                Free Scorecard
              </Link>
            )}
            {user ? (
              <button type="button" onClick={() => { setOpen(false); handleLogout(); }} className="btn-secondary text-center justify-center">Logout</button>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-secondary text-center justify-center">Login</Link>
                <Link to="/contact" onClick={() => setOpen(false)} className="btn-primary text-center justify-center">Request Demo</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
